// React imports
import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

// Generic imports
import axios from 'axios';
import _ from 'lodash';

// MUI Imports
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import LinearProgress from '@mui/material/LinearProgress';

function DeprecatedRates() {
    const { accesstoken } = useOutletContext();
    const [organizations, setOrganizations] = useState(null);
    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState({});
    const [confirmDialog, setConfirmDialog] = useState(null);

    // Delete by a chosen expiry date, for one workspace.
    const [cutoffOrg, setCutoffOrg] = useState('');
    const [cutoff, setCutoff] = useState('');
    const [cutoffResult, setCutoffResult] = useState(null); // { orgId, cutoff, count, ids, byMonth }
    const [cutoffLoading, setCutoffLoading] = useState(false);
    const [cutoffError, setCutoffError] = useState(null);
    const [cutoffConfirm, setCutoffConfirm] = useState(false);
    const [cutoffProgress, setCutoffProgress] = useState(null); // { done, total }
    const [cutoffDone, setCutoffDone] = useState(null);

    useEffect(() => {
        (async () => {
            const { data: _organizations } = await axios.get(`/api/organizations?accesstoken=${accesstoken}`);
            setOrganizations(_organizations);

            const initialLoading = {};
            _organizations.forEach(org => { initialLoading[org.id] = true; });
            setLoading(initialLoading);

            await Promise.all(_organizations.map(async (org) => {
                try {
                    const { data } = await axios.get(`/api/rates/deprecated-rates/${org.id}?accesstoken=${accesstoken}`);
                    setStats(prev => ({ ...prev, [org.id]: data }));
                } catch (e) {
                    console.error(`Error fetching deprecated rates for ${org.id}`, e);
                }
                setLoading(prev => ({ ...prev, [org.id]: false }));
            }));
        })();
    }, []);

    async function fetchOrgStats(orgId) {
        setLoading(prev => ({ ...prev, [orgId]: true }));
        try {
            const { data } = await axios.get(`/api/rates/deprecated-rates/${orgId}?accesstoken=${accesstoken}`);
            setStats(prev => ({ ...prev, [orgId]: data }));
        } catch (e) {
            console.error(`Error fetching deprecated rates for ${orgId}`, e);
        }
        setLoading(prev => ({ ...prev, [orgId]: false }));
    }

    async function countUntil() {
        setCutoffLoading(true);
        setCutoffError(null);
        setCutoffResult(null);
        setCutoffDone(null);
        try {
            const { data } = await axios.get(`/api/rates/deprecated-rates/${cutoffOrg}?accesstoken=${accesstoken}&until=${cutoff}`);
            setCutoffResult({ orgId: cutoffOrg, ...data.expiredUntil });
        } catch (e) {
            setCutoffError(e.response?.data?.detail || e.message);
        }
        setCutoffLoading(false);
    }

    async function deleteUntil() {
        const { orgId, ids } = cutoffResult;
        setCutoffConfirm(false);
        setCutoffError(null);
        setCutoffProgress({ done: 0, total: ids.length });
        let done = 0;
        for (const chunk of _.chunk(ids, 25)) {
            try {
                await axios.post(`/api/rates/admin-delete?organizationId=${orgId}&accesstoken=${accesstoken}`, { rates: chunk });
            } catch (e) {
                setCutoffError(`Stopped after ${done} of ${ids.length}: ${e.response?.data?.detail || e.message}. Count again to see what is left.`);
                setCutoffProgress(null);
                return;
            }
            done += chunk.length;
            setCutoffProgress({ done, total: ids.length });
        }
        setCutoffProgress(null);
        setCutoffDone(`Deleted ${done} rates.`);
        setCutoffResult(null);
        await fetchOrgStats(orgId);
    }

    async function handleDelete(orgId) {
        const orgStats = stats[orgId];
        if (!orgStats || !orgStats.expired3Plus.ids.length) return;

        setConfirmDialog(null);
        setLoading(prev => ({ ...prev, [orgId]: true }));

        const ids = orgStats.expired3Plus.ids;
        const chunks = _.chunk(ids, 25);
        for (const chunk of chunks) {
            await axios.post(`/api/rates/admin-delete?organizationId=${orgId}&accesstoken=${accesstoken}`, { rates: chunk });
        }

        await fetchOrgStats(orgId);
    }

    if (!organizations) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ m: 2 }}>
            <h1>Deprecated Rates</h1>
            <Paper variant='outlined' sx={{ p: 2, mb: 3, maxWidth: 820, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant='h6' sx={{ fontWeight: 700 }}>Delete rates expiring on or before a date</Typography>
                <Typography variant='body2' color='text.secondary'>
                    Pick a workspace and a date, count first, then delete. Rates are removed from the database permanently, and the workspace's users reload their rates.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField select size='small' label='Workspace' value={cutoffOrg} onChange={e => { setCutoffOrg(e.target.value); setCutoffResult(null); }} sx={{ minWidth: 240 }}>
                        {_.sortBy(organizations || [], o => (o.alias || '').toLowerCase()).map(o => <MenuItem key={o.id} value={o.id}>{o.alias}</MenuItem>)}
                    </TextField>
                    <TextField size='small' type='date' label='Expiring on or before' InputLabelProps={{ shrink: true }} value={cutoff} onChange={e => { setCutoff(e.target.value); setCutoffResult(null); }} />
                    <Button variant='outlined' onClick={countUntil} disabled={!cutoffOrg || !cutoff || cutoffLoading || !!cutoffProgress}>
                        {cutoffLoading ? <CircularProgress size={18} /> : 'Count'}
                    </Button>
                </Box>
                {cutoffResult && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant='body2'>
                            <b>{cutoffResult.count.toLocaleString()}</b> rates in <b>{organizations.find(o => o.id === cutoffResult.orgId)?.alias}</b> expire on or before <b>{cutoffResult.cutoff}</b>.
                        </Typography>
                        {cutoffResult.count > 0 && (
                            <Typography variant='caption' color='text.secondary'>
                                By month: {Object.entries(cutoffResult.byMonth).sort().map(([m, n]) => `${m}: ${n.toLocaleString()}`).join(' · ')}
                            </Typography>
                        )}
                        <Box>
                            <Button variant='contained' color='error' disabled={!cutoffResult.count || !!cutoffProgress} onClick={() => setCutoffConfirm(true)}>
                                Delete {cutoffResult.count.toLocaleString()} rates
                            </Button>
                        </Box>
                    </Box>
                )}
                {cutoffProgress && (
                    <Box>
                        <Typography variant='body2'>Deleting {cutoffProgress.done.toLocaleString()} of {cutoffProgress.total.toLocaleString()}… keep this page open.</Typography>
                        <LinearProgress variant='determinate' value={(cutoffProgress.done / cutoffProgress.total) * 100} />
                    </Box>
                )}
                {cutoffError && <Alert severity='error'>{cutoffError}</Alert>}
                {cutoffDone && <Alert severity='success'>{cutoffDone}</Alert>}
            </Paper>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Organization</TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>Total</TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>Expired 3+ mo</TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>Expired 1-3 mo</TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>Active</TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {organizations.map(org => (
                            <TableRow key={org.id}>
                                <TableCell>{org.alias}</TableCell>
                                {loading[org.id] ? (
                                    <TableCell colSpan={5} sx={{ textAlign: 'center' }}>
                                        <CircularProgress size={20} />
                                    </TableCell>
                                ) : stats[org.id] ? (
                                    <>
                                        <TableCell sx={{ textAlign: 'center' }}>{stats[org.id].total}</TableCell>
                                        <TableCell sx={{ textAlign: 'center', color: stats[org.id].expired3Plus.count > 0 ? 'red' : 'inherit' }}>
                                            {stats[org.id].expired3Plus.count}
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>{stats[org.id].expired1to3.count}</TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>{stats[org.id].active.count}</TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>
                                            <Button
                                                variant='contained'
                                                color='error'
                                                size='small'
                                                disabled={!stats[org.id].expired3Plus.count}
                                                onClick={() => setConfirmDialog(org.id)}
                                            >
                                                Delete
                                            </Button>
                                        </TableCell>
                                    </>
                                ) : (
                                    <TableCell colSpan={5} sx={{ textAlign: 'center' }}>Error loading</TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Dialog open={cutoffConfirm} onClose={() => setCutoffConfirm(false)}>
                <DialogTitle>Delete {cutoffResult?.count.toLocaleString()} rates permanently?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Every rate in {organizations.find(o => o.id === cutoffResult?.orgId)?.alias} that expires on or before {cutoffResult?.cutoff} will be removed from the database. This can't be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCutoffConfirm(false)}>Cancel</Button>
                    <Button onClick={deleteUntil} color='error' variant='contained'>Delete {cutoffResult?.count.toLocaleString()} rates</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={!!confirmDialog} onClose={() => setConfirmDialog(null)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete {stats[confirmDialog]?.expired3Plus.count} rates that expired 3+ months ago?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialog(null)}>Cancel</Button>
                    <Button onClick={() => handleDelete(confirmDialog)} color='error' variant='contained'>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default DeprecatedRates;
